// @flow
import type {
  ManualOrderProductOptionGroup,
  SelectedProductOptions,
  ValidationError,
  ProductOptionGroupValidationErrors,
  ValidatorFunction,
} from '../types/productOptions';

const validateRequired: ValidatorFunction = (group, selectedOptionsData) => {
  const errors: ValidationError[] = [];

  if (group.required && selectedOptionsData.length === 0) {
    errors.push({
      type: 'required',
      message: `${group.storeProductOptionGroup.name} es obligatorio`,
    });
  }

  return errors;
};

const validateQuantityConstraints: ValidatorFunction = (
  group,
  selectedOptionsData
) => {
  const errors: ValidationError[] = [];

  if (!group.config || selectedOptionsData.length === 0) {
    return errors;
  }

  const {minQuantity, maxQuantity} = group.config;
  const totalQuantity = selectedOptionsData.reduce(
    (sum, option) => sum + option.quantity,
    0
  );

  if (typeof minQuantity === 'number' && totalQuantity < minQuantity) {
    errors.push({
      type: 'minQuantity',
      message: `${
        group.storeProductOptionGroup.name
      } requiere al menos ${minQuantity} ${
        minQuantity === 1 ? 'unidad' : 'unidades'
      }`,
    });
  }

  if (typeof maxQuantity === 'number' && totalQuantity > maxQuantity) {
    errors.push({
      type: 'maxQuantity',
      message: `${
        group.storeProductOptionGroup.name
      } permite máximo ${maxQuantity} ${
        maxQuantity === 1 ? 'unidad' : 'unidades'
      }`,
    });
  }

  return errors;
};

const FIELD_VALIDATORS: {[string]: ValidatorFunction[]} = {
  TEXT: [validateRequired],
  NUMERIC: [validateRequired],
  LISTING: [validateRequired, validateQuantityConstraints],
  GRID: [validateRequired, validateQuantityConstraints],
};

function getValidatorForFieldType(
  fieldType: string,
  group: ManualOrderProductOptionGroup,
  selectedOptionsData: Array<{
    optionId: string,
    quantity: number,
    textValue: string,
  }>
): ValidationError[] {
  const validators = FIELD_VALIDATORS[fieldType] || [validateRequired];

  const errors: ValidationError[] = [];

  validators.forEach(validator => {
    errors.push(...validator(group, selectedOptionsData));
  });
  return errors;
}

export function validateProductOptionGroup(
  group: ManualOrderProductOptionGroup,
  selectedOptions: SelectedProductOptions
): ValidationError[] {
  const groupId = group.id;
  const groupSelections = selectedOptions[groupId] || {};

  const selectedOptionsData = Object.entries(groupSelections)
    .filter(([, selection]) => selection.selected)
    .map(([optionId, selection]) => ({
      optionId,
      quantity: selection.quantity || 0,
      textValue: selection.textValue || '',
    }));

  return getValidatorForFieldType(group.fieldType, group, selectedOptionsData);
}

export function validateAllProductOptionGroups(
  groups: ManualOrderProductOptionGroup[],
  selectedOptions: SelectedProductOptions
): ProductOptionGroupValidationErrors {
  const validationErrors: ProductOptionGroupValidationErrors = {};

  groups.forEach(group => {
    const groupErrors = validateProductOptionGroup(group, selectedOptions);
    if (groupErrors.length > 0) {
      validationErrors[group.id] = groupErrors;
    }
  });

  return validationErrors;
}

export function hasValidationErrors(
  validationErrors: ProductOptionGroupValidationErrors
): boolean {
  return Object.keys(validationErrors).length > 0;
}

export function getAllErrorMessages(
  validationErrors: ProductOptionGroupValidationErrors
): string[] {
  const messages: string[] = [];

  Object.values(validationErrors).forEach(groupErrors => {
    groupErrors.forEach(error => {
      messages.push(error.message);
    });
  });

  return messages;
}
